import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getStatusMessage } from './statusMessages';
import { API_ENDPOINTS } from './apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Signup.css';
import { useAuth } from "./AuthContext";

function Signup() {
  const navigate = useNavigate();
  const symbols = ['💖', '🌸', '💗', '💞', '🌼', '💑', '💍'];
  const { login, token } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [animateArrow, setAnimateArrow] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (token && token.trim() !== '') {
      navigate('/search');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (showTermsPopup) {
      setTimeout(() => {
        const bodyEl = document.querySelector('.modal-body');
        if (bodyEl && bodyEl.scrollHeight <= bodyEl.clientHeight) {
          setScrolledToBottom(true);
        }
      }, 100);
    }
  }, [showTermsPopup]);

  const validateEmail = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return 'Email is required';
    if (trimmed.length > 100) return 'Email too long';
    if (!/^[^\x00-\x1F\x7F\\"]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Invalid email';
    return '';
  };

  const validatePassword = (val) => {
    const trimmed = val.trim();
    if (trimmed.length < 8 || trimmed.length > 12) return 'Password must be 8–12 characters';
    if (!/^[\x20-\x7E]*$/.test(trimmed)) return 'Invalid characters in password';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimateArrow(true);
    setErrors([]);
    setMessage('');
    setLoading(true);

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError || passwordError || !acceptedTerms) {
      setTimeout(() => setAnimateArrow(false), 800);
      const termsError = !acceptedTerms ? 'You must agree to the Terms & Conditions' : '';
      setErrors([emailError, passwordError, termsError].filter(Boolean));
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(API_ENDPOINTS.SIGNUP, { email, password });
      const { result, user, otp, errors: serverErrors } = res.data;

      setTimeout(() => setAnimateArrow(false), 800);

      if (result === 1) {
        if (otp === 6) {
          setErrors([getStatusMessage(otp)]);
          setLoading(false);
          return;
        }
        const otpMsg = getStatusMessage(otp);
        setMessage(otpMsg.trim());

        setTimeout(() => {
          navigate('/VerifyOTP', { state: { email, rotp: otp } });
        }, 100);
      } else {
        const allMessages = [];
        if (Array.isArray(serverErrors)) allMessages.push(...serverErrors);
        if (user !== undefined) allMessages.push(getStatusMessage(user));
        if (otp !== undefined) allMessages.push(getStatusMessage(otp));
        setErrors(allMessages.length ? allMessages : ['Signup failed.']);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      setTimeout(() => setAnimateArrow(false), 800);

      if (status === 400 && Array.isArray(data?.errors)) {
        setErrors(data.errors);
      } else if (status === 400 && (data?.user || data?.otp)) {
        const failMessages = [];
        if (typeof data.user === 'number') failMessages.push(getStatusMessage(data.user));
        if (typeof data.otp === 'number') failMessages.push(getStatusMessage(data.otp));
        setErrors(failMessages);
      } else {
        setErrors(['Server error. Please try again later.']);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
<div>

    <div className="animated-bg d-flex flex-column min-vh-100 p-4" style={{ background: 'linear-gradient(135deg, #ffe0f0, #d0e8ff)' }}>
      {/* SEO Meta Tags */}
 
<Helmet>
  <title>Indian Matrimonial | LuvMarry™ - Trusted Indian Matrimony</title>

  <meta
    name="description"
    content="Join LuvMarry – the #1 Indian Matrimonial and Marriage Finder. Find verified Indian brides and grooms by religion, caste, or profession. 100% free registration."
  />

  <meta
    name="keywords"
    content="Indian matrimonial, Indian matrimony, Indian marriage site, Indian marriage finder, Indian brides, Indian grooms, free Indian matrimony registration, LuvMarry signup, Indian marriage portal, Indian singles, desi marriage site, Indian matchmaking"
  />

  <link rel="canonical" href="https://luvmarry.com/signup" />
  <meta name="robots" content="index, follow" />

  {/* Open Graph / Facebook */}
  <meta property="og:title" content="Join LuvMarry – Trusted Indian Matrimonial 💖" />
  <meta
    property="og:description"
    content="Register free on LuvMarry to find verified Indian brides and grooms. Trusted Indian matrimonial site for serious relationships and marriage."
  />
  <meta property="og:image" content="/LuvMarryLogoPink.png" />
  <meta property="og:url" content="https://luvmarry.com/signup" />
  <meta property="og:type" content="website" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="LuvMarry – Indian Matrimonial for Serious Relationships" />
  <meta
    name="twitter:description"
    content="Find your perfect Indian match on LuvMarry. 100% free matrimonial site for Indian singles seeking meaningful marriage."
  />
  <meta name="twitter:image" content="/LuvMarryLogoPink.png" />

  {/* Schema.org JSON-LD */}
  <script type="application/ld+json">{`
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "LuvMarry",
      "url": "https://luvmarry.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://luvmarry.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      },
      "sameAs": [
        "https://www.facebook.com/luvmarry",
        "https://www.instagram.com/luvmarry",
        "https://www.linkedin.com/company/luvmarry"
      ],
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is LuvMarry free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, LuvMarry offers 100% free registration for all members. You can browse and connect with Indian matrimonial profiles without any subscription fees."
          }
        },
        {
          "@type": "Question",
          "name": "Is LuvMarry a trusted Indian matrimonial website?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "LuvMarry is a verified and trusted Indian matrimonial platform connecting genuine brides and grooms who are serious about marriage."
          }
        },
        {
          "@type": "Question",
          "name": "How do I find Indian brides and grooms on LuvMarry?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "After signing up, you can search for Indian brides and grooms by religion, caste, profession, or city using advanced filters on LuvMarry."
          }
        }
    } 
  `}</script>
</Helmet>


      {/* Floating background symbols */}
      {Array.from({ length: 30 }).map((_, i) => {
        const symbol = symbols[Math.floor(Math.random() * symbols.length)];
        return (
          <span
            key={i}
            className="position-absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${12 + Math.random() * 14}px`,
              opacity: 0.04,
              animation: 'floatDown 20s linear infinite',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            {symbol}
          </span>
        );
      })}

      {/* Signup Form */}
      <div className="container" style={{ maxWidth: 500, zIndex: 2 }}>
        <div className="text-center mb-4">
          <span className="badge bg-secondary">Step 1 of 4</span>
          <h6 className="mt-2 wedding-heading">Signup for LuvMarry – Indian Matrimony </h6>
          <p className="text-muted">Find Indian Singles, Brides & Grooms 💗</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label htmlFor="email" className="form-label text-muted">Email address</label>
            <input
              id="email"
              type="email"
              className="form-control"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label text-muted">Password</label>
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder="8–12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="termsCheck"
              checked={acceptedTerms}
              onChange={(e) => {
                if (!acceptedTerms) {
                  setShowTermsPopup(true);
                  setScrolledToBottom(false);
                } else {
                  setAcceptedTerms(false);
                }
              }}
            />
            <label className="form-check-label" htmlFor="termsCheck">
              I agree to the{' '}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => {
                  setShowTermsPopup(true);
                  setScrolledToBottom(false);
                }}
              >
                Terms & Conditions
              </button>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn w-100 d-flex justify-content-between align-items-center mt-2"
            style={{ backgroundColor: '#ff69b4', color: 'white' }}
            disabled={loading || !acceptedTerms}
          >
            <span>{loading ? 'Creating account...' : 'Continue to OTP'}</span>
            <i
              className={`bi bi-arrow-right-circle-fill ${animateArrow ? 'move-arrow' : ''}`}
              style={{ fontSize: '1.4rem' }}
            ></i>
          </button>
        </form>

        {/* Messages */}
        {message && (
          <div className="alert alert-success mt-3" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </div>
        )}

        {errors.length > 0 && (
          <div className="alert alert-danger mt-3">
            <ul className="mb-0">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}

        {/* Login Link */}
        <div className="text-center mt-4">
          <small className="text-muted">
            Already have an account?{' '}
            <a href="/login" className="text-primary">Log in</a>
          </small>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsPopup && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
          onClick={() => setShowTermsPopup(false)}
        >
          <div
            className="modal-dialog modal-dialog-scrollable modal-lg modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header bg-light border-0" style={{ backgroundColor: '#fff0f6' }}>
                <h2 className="modal-title text-pink fw-bold fs-5">
                  <i className="bi bi-bookmark-heart-fill me-2"></i>Terms & Conditions
                </h2>
                <button type="button" className="btn-close" onClick={() => setShowTermsPopup(false)} aria-label="Close"></button>
              </div>

              <div
                className="modal-body text-muted"
                style={{ maxHeight: '60vh', overflowY: 'auto' }}
                onScroll={(e) => {
                  const el = e.target;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                    setScrolledToBottom(true);
                  }
                }}
              >
                {/* List items remain same */}
                <div className="list-group">
                  <div className="list-group-item"><i className="bi bi-person-check-fill text-success me-2"></i>You must be 18+ years old.</div>
                  <div className="list-group-item"><i className="bi bi-heart-fill text-danger me-2"></i>Respect other users.</div>
                  <div className="list-group-item"><i className="bi bi-person-x-fill text-warning me-2"></i>No fake profiles.</div>
                  <div className="list-group-item"><i className="bi bi-lock-fill text-dark me-2"></i>Never share sensitive information.</div>
                  <div className="list-group-item"><i className="bi bi-shield-x text-danger me-2"></i>No scamming or explicit content.</div>
                </div>
                <div className="mt-3 text-center">❤️ Be honest and kind. Find meaningful connections.</div>
              </div>

              <div className="modal-footer bg-light border-0 d-flex flex-column flex-md-row gap-2">
                <button
                  className="btn btn-outline-secondary w-100 rounded-pill"
                  onClick={() => {
                    setShowTermsPopup(false);
                    setScrolledToBottom(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn w-100 rounded-pill"
                  disabled={!scrolledToBottom}
                  style={{ backgroundColor: '#ff69b4', borderColor: '#ff69b4' }}
                  onClick={() => {
                    setAcceptedTerms(true);
                    setShowTermsPopup(false);
                  }}
                >
                  I Agree 💖
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>



{/* SEO text block */}
 <section className="signup-seo-content container py-5">
  <h1>Indian Matrimonial – Find Your Perfect Match on LuvMarry</h1>
  <p>
    <strong>LuvMarry</strong> is a trusted <em>Indian Matrimonial</em> and matchmaking platform
    that helps you find your life partner from verified Indian brides and grooms. Whether you’re
    looking for an <strong>arranged marriage</strong> or a <strong>love-based match</strong>,
    LuvMarry connects people from diverse religions, castes, and professions.
  </p>

  <h2>Why Choose LuvMarry Matrimony</h2>
  <ul>
    <li>✔ 100% Free Matrimonial Registration – No subscription fees</li>
    <li>✔ Verified Indian Brides and Grooms across India and worldwide</li>
    <li>✔ Match by Religion, Caste, Profession, or City</li>
    <li>✔ Safe and Private Messaging between members</li>
  </ul>

  <h2>Start Your Marriage Journey Today</h2>
  <p>
    Begin your journey to love and marriage with <strong>LuvMarry</strong> – one of the most
    trusted <em>Indian matrimonial websites</em>. Join now to meet compatible Indian singles from
    every region and background. Every profile is verified for authenticity and seriousness toward marriage.
  </p>

  <h3>Popular Searches</h3>
  <ul>
    <li><a href="/search?type=brides">Find Indian Brides</a></li>
    <li><a href="/search?type=grooms">Find Indian Grooms</a></li>
    <li><a href="/search">Browse All Indian Matrimonial Profiles</a></li>
  </ul>

  <p className="mt-3">
    Create your free profile today on <strong>LuvMarry.com</strong> and discover genuine,
    meaningful connections through the most trusted <strong>Indian Matrimonial Platform</strong>.
  </p>
</section>



</div>
    
    
  );
}

export default Signup;
